import DOMPurify from "isomorphic-dompurify";

// Force rel="noopener noreferrer" on any link that opens a new browsing context, so
// attacker-authored `<a target="_blank">` can't reverse-tabnab the opener tab (ERI-1653).
// Registered once at module load (isomorphic-dompurify shares one global instance).
DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  if (node.tagName === "A" && node.hasAttribute("target")) {
    node.setAttribute("rel", "noopener noreferrer");
  }
});

/**
 * Sanitize user-authored rich-text HTML (TipTap editor output) before it is
 * rendered via `dangerouslySetInnerHTML`.
 *
 * Workshop descriptions and instructor bios are authored by self-serve,
 * paying-but-not-admin workshop-leader accounts and rendered on PUBLIC,
 * unauthenticated pages. A leader can bypass the editor UI and PATCH raw HTML
 * straight to their row via the Supabase REST API, so the only safe place to
 * trust this content is at the render boundary. (ERI-1538 H6 / SEC-H1 / SEC-H2)
 *
 * Strict allowlist: formatting tags only. No `script`/`svg`/`iframe`/`object`,
 * no `on*` event-handler attributes, no `javascript:`/`data:` URLs.
 */
const ALLOWED_TAGS = [
  "p", "br", "hr",
  "b", "strong", "i", "em", "u", "s", "strike", "sub", "sup", "mark",
  "h1", "h2", "h3", "h4", "h5", "h6",
  "ul", "ol", "li",
  "blockquote", "pre", "code",
  "a", "span",
];

const ALLOWED_ATTR = ["href", "title", "target", "rel"];

export function sanitizeRichText(dirty: string | null | undefined): string {
  if (!dirty) return "";
  return DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOW_DATA_ATTR: false,
    // DOMPurify blocks javascript:/vbscript: and unsafe data: URIs by default;
    // the tag/attr allowlist above strips all event handlers and script-bearing
    // elements (svg/iframe/object/script).
  });
}
